import React, { useState } from "react";
import {
  ArrowPathIcon,
  ListBulletIcon,
  MapIcon,
} from "@heroicons/react/24/outline";
import CoverageBreakdown from "../../components/subComponents/CoverageBreakdown";
import CoverageMap from "../../components/subComponents/CoverageMap";
import { formatPlayDuration } from "../../components/subComponents/campaignFormatting";
import {
  useBrandCampaignSummary,
  useBrandScreenMap,
} from "../../hooks/useBrandCampaigns";
import { isBrandDemoData } from "../../mocks/brandCampaignDemo";

interface SummaryStripItem {
  label: string;
  value: string | number;
  hint?: string;
  accent?: boolean;
}

/**
 * The headline numbers on one line. Both views need their vertical space — the
 * map to be visible without scrolling, the coverage accordion to show more than
 * a couple of cities — and each repeats these counts in its own terms anyway.
 */
const SummaryStrip: React.FC<{ items: SummaryStripItem[] }> = ({ items }) => (
  <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm shadow-sm">
    {items.map((item) => (
      <div key={item.label} className="flex items-baseline gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {item.label}
        </span>
        <span
          className={`font-bold ${item.accent ? "text-red-600" : "text-gray-900"}`}
        >
          {item.value}
        </span>
        {item.hint && (
          <span className="text-xs text-gray-500">({item.hint})</span>
        )}
      </div>
    ))}
  </div>
);

/**
 * The brand's campaign view: where its creatives are running, and for how long.
 *
 * A "campaign" is derived, not stored — the backend walks creative -> playlist
 * -> schedule -> screen. So this page shows placements a screen genuinely holds
 * rather than a plan that may have been superseded.
 *
 * Both views answer that from one walk of the fleet: the map pins the screens,
 * the list nests them city -> area -> screen -> campaign. A brand asks "where
 * is my ad and is it playing", which is a question about places, so there is
 * deliberately no creative-first table here.
 *
 * Read-only, like the rest of the brand portal: the media house owns the
 * creatives and the scheduling.
 */
const BrandCampaigns: React.FC = () => {
  // The map is the brand's first question ("where is my ad?"), so it opens
  // first; the coverage list is one click away.
  const [view, setView] = useState<"list" | "map">("map");

  const { summary, refetch: refetchSummary } = useBrandCampaignSummary();
  const screenMap = useBrandScreenMap();

  /**
   * Refresh is tracked locally rather than off react-query's flags.
   *
   * `isLoading` is false for a refetch once the first load has succeeded, so it
   * never says "the user pressed Refresh". Without this the button fires its
   * requests and then nothing on screen moves, which reads as broken. The
   * timestamp is the proof the fetch actually happened even when the data came
   * back identical.
   */
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([refetchSummary(), screenMap.refetch()]);
      setRefreshedAt(new Date());
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="flex flex-col px-6 py-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-800">
            My Campaigns
            {isBrandDemoData() && (
              <span className="rounded bg-warning-light px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800">
                Demo data
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500">
            Where each of your creatives is running, and for how long.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div
            role="group"
            aria-label="View"
            className="flex overflow-hidden rounded border border-gray-300 bg-white"
          >
            {(
              [
                { value: "list", label: "List", Icon: ListBulletIcon },
                { value: "map", label: "Map", Icon: MapIcon },
              ] as const
            ).map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                aria-pressed={view === value}
                onClick={() => setView(value)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition ${
                  view === value
                    ? "bg-primary-default text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
          {refreshedAt && (
            <span className="text-xs text-gray-500">
              Updated{" "}
              {refreshedAt.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ArrowPathIcon
              className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            {isRefreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {summary && (
        <SummaryStrip
          items={[
            {
              label: "Campaigns",
              value: summary.totalCampaigns,
              hint: `${summary.activeCampaigns} active · ${summary.upcomingCampaigns} upcoming`,
            },
            { label: "Screens reached", value: summary.totalScreens },
            {
              label: "On air now",
              value: summary.liveScreens,
              accent: summary.liveScreens > 0,
            },
            {
              label: "Total plays",
              value: summary.totalPlays.toLocaleString(),
              hint: formatPlayDuration(summary.totalPlayDuration),
            },
          ]}
        />
      )}

      {view === "map" && (
        <CoverageMap
          screens={screenMap.screens}
          truncated={screenMap.truncated}
          isLoading={screenMap.isLoading}
          isError={screenMap.isError}
        />
      )}

      {view === "list" && (
        <CoverageBreakdown
          screens={screenMap.screens}
          truncated={screenMap.truncated}
          isLoading={screenMap.isLoading}
          isError={screenMap.isError}
        />
      )}
    </div>
  );
};

export default BrandCampaigns;
